import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';

import classnames from 'classnames';
import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { noop } from 'ui-components/default-props/noop';
import { Button } from 'ui-components/components/button';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { Text, TextType } from 'ui-components/components/text';
import { NotificationService } from 'ui-components/services/notification-service';

import { copyToClipboard } from '../../../../../../services/copy-to-clipboard';

import style from './style.module.less';

/**
 * @typedef {import('../../components/text/text-types.d.ts').TextValue} TextValue
 */

const messages = defineMessages({
  copyToClipboard: {
    description: 'Tooltip message for copy URL button',
    defaultMessage: 'Copy to Clipboard',
    id: 'srJHD4',
  },
  copied: {
    description: 'Success message after copying URL to clipboard',
    defaultMessage: 'Copied to clipboard!',
    id: '7ZC9HG',
  },
  defaultLabel: {
    description: 'Default label for the portal URL display',
    defaultMessage: 'Return Portal URL',
    id: 'M6DGGm',
  },
  returnReminder: {
    description: 'Default message reminding users to embed the link',
    defaultMessage: 'Embed this link on your website so your customers can submit their return requests.',
    id: 'opfUmt',
  },
});

/**
 * @param {object} props
 * @param {string} [props.className] - Optional CSS class for the root element
 * @param {string} [props.urlWrapperClassName] - Optional CSS class for the URL wrapper element
 * @param {string} [props.urlClassName] - Optional CSS class for the URL display element
 * @param {string} props.url - The portal URL
 * @param {TextValue} [props.labelMessage] - The label for the portal URL
 * @param {TextValue} [props.reminderMessage] - The reminder message for the portal URL
 * @param {() => void} [props.trackCopyUrl]
 * @returns {JSX.Element} The branded returns component
 */
export const ReadOnlyPortalUrl = ({
  className,
  urlWrapperClassName,
  urlClassName,
  url,
  labelMessage = messages.defaultLabel,
  reminderMessage = messages.returnReminder,
  trackCopyUrl = noop,
}) => {
  const onCopyClick = useCallback(() => {
    copyToClipboard(url);

    trackCopyUrl(url);

    NotificationService.showNotification({
      title: '',
      notificationType: 'info',
      content: <FormattedMessage {...messages.copied} />,
      duration: 3000,
    });
  }, [trackCopyUrl, url]);

  return (
    <div className={className}>
      <Text textType={TextType.Label} value={labelMessage} />
      <div className={classnames(style['url-wrapper'], urlWrapperClassName)}>
        <div className={classnames(style.url, urlClassName)} id='url'>
          {url}
        </div>
        <Tooltip>
          <Button className={style['copy-button']} onClick={onCopyClick} theme='primary'>
            <FontAwesomeIcon icon={faCopy} />
          </Button>
          <TooltipContent>
            <FormattedMessage {...messages.copyToClipboard} />
          </TooltipContent>
        </Tooltip>
      </div>
      <Text className={style.reminder} textType={TextType.Caption} value={reminderMessage} />
    </div>
  );
};
